import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import {
  isGmdssExpiryDate,
  parseGmdssExpiry,
} from "src/generator/utils/gmdssHelpers";
import { useCheckPerm, useIsOnline } from "src/hooks";
import {
  type IGmdssItem,
  type IGmdssSetupForm,
} from "src/pages/Settings/YachtSetup/GmdssSetup/GmdssSetup.types";
import { toMantineDateString } from "src/utils/mantineDates";

const SECTIONS = ["epirb", "sat", "vhf"] as const;
type Section = (typeof SECTIONS)[number];

const SECTION_LABELS: Record<Section, string> = {
  epirb: "EPIRB",
  sat: "SART",
  vhf: "VHF",
};

interface Props {
  data: IGmdssSetupForm;
}

const GmdssDetailsForm = ({ data }: Props) => {
  const { register, reset, getValues, control } =
    useFormContext<IGmdssSetupForm>();
  const isOnline = useIsOnline();
  const checkPerm = useCheckPerm();
  const readOnly = !checkPerm("editparticulars") || !isOnline;

  const [counts, setCounts] = useState<Record<Section, number>>({
    epirb: data.epirb?.length || 1,
    sat: data.sat?.length || 1,
    vhf: data.vhf?.length || 1,
  });

  const handleAdd = (section: Section) => {
    const currentValues = getValues();
    const existing: IGmdssItem[] = currentValues[section] ?? [];
    reset({
      ...currentValues,
      [section]: [...existing, { serial: "", exp: "" }],
    });
    setCounts(prev => ({ ...prev, [section]: prev[section] + 1 }));
  };

  const handleRemove = (section: Section, index: number) => {
    const currentValues = getValues();
    const existing: IGmdssItem[] = currentValues[section] ?? [];
    const updated = existing.filter((_, i) => i !== index);
    reset({ ...currentValues, [section]: updated });
    setCounts(prev => ({ ...prev, [section]: prev[section] - 1 }));
  };

  const checkIfDateOrEmpty = (section: Section, index: number) => {
    const value = getValues(`${section}.${index}.exp`);
    if (!value) return true;
    return isGmdssExpiryDate(value);
  };

  return (
    <Flex direction="column" w="100%" mt={30} gap={24} px={16}>
      <Flex direction="column" gap={8} maw={400} mx="auto" w="100%">
        <TextInput label="Owner" readOnly={readOnly} {...register("owner")} />
        <TextInput label="SBM" readOnly={readOnly} {...register("sbm")} />
      </Flex>

      <SimpleGrid
        cols={3}
        spacing={40}
        breakpoints={[{ maxWidth: 940, cols: 1 }]}>
        {SECTIONS.map(section => (
          <Flex key={section} direction="column" gap={16}>
            <Divider
              label={SECTION_LABELS[section]}
              labelPosition="center"
              mb={4}
            />
            {Array.from({ length: counts[section] }, (_, i) => (
              <Flex key={i} direction="row" gap={4} align="center">
                <Flex gap={4} style={{ flex: 1, flexWrap: "wrap" }}>
                  <TextInput
                    label="Serial Number"
                    readOnly={readOnly}
                    miw={200}
                    style={{ flex: 1 }}
                    {...register(`${section}.${i}.serial`)}
                  />
                  {checkIfDateOrEmpty(section, i) ? (
                    <Controller
                      name={`${section}.${i}.exp`}
                      control={control}
                      render={({ field }) => {
                        const dateValue = field.value
                          ? toMantineDateString(parseGmdssExpiry(field.value))
                          : null;
                        return (
                          <DatePickerInput
                            label="Battery Expiry"
                            readOnly={readOnly}
                            miw={200}
                            style={{ flex: 1 }}
                            valueFormat="DD MMM YYYY"
                            value={dateValue}
                            onChange={val =>
                              field.onChange(
                                val ? dayjs(val).format("DD MMM YYYY") : "",
                              )
                            }
                            clearable
                          />
                        );
                      }}
                    />
                  ) : (
                    <TextInput
                      label="Battery Expiry"
                      readOnly={readOnly}
                      miw={200}
                      style={{ flex: 1 }}
                      {...register(`${section}.${i}.exp`)}
                    />
                  )}
                </Flex>
                {!readOnly && (
                  <ConfirmationButton
                    modalText={`Are you sure you want to remove this ${SECTION_LABELS[section]}?`}
                    onConfirm={() => handleRemove(section, i)}
                    buttonProps={{
                      variant: "transparent",
                      disabled: counts[section] <= 1,
                      style: {
                        backgroundColor: "transparent",
                        marginTop: 24,
                        paddingRight: 0,
                        paddingLeft: 12,
                      },
                    }}>
                    <ActionIcon color="red" disabled={counts[section] <= 1}>
                      <IconTrash size={24} />
                    </ActionIcon>
                  </ConfirmationButton>
                )}
              </Flex>
            ))}
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                mt={4}
                onClick={() => handleAdd(section)}>
                Add {SECTION_LABELS[section]}
              </Button>
            )}
          </Flex>
        ))}
      </SimpleGrid>
    </Flex>
  );
};

export default GmdssDetailsForm;
